import React from "react";
import { Box, Checkbox, Grid2, Divider, useTheme } from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import { InventoryOpsSkuVariantLine } from "../../../../../../models/ProjectManagement/InventoryOpsRequests";
import InlineFormError from "./InlineFormError";

export interface SkuQtyRow {
  variant_id: number;
  sku: string;
  title?: string | null;
  /** Quantity ordered for this SKU (from size_mixes). */
  orderedQty?: number;
  /**
   * Reference column qty (Ordered for raw inward, or prior-step qty for later
   * sections such as Raw Inward / Print Outward).
   */
  referenceQty?: number;
  /** Header label for the reference column. */
  referenceLabel?: string;
  /** Quantity already recorded for this SKU (received / rejected / sent). */
  recordedQty?: number;
  /** Remaining quantity allowed for this step. */
  remainingQty?: number;
  /** Max qty the admin can enter (same as remainingQty when set). */
  maxQty?: number;
  /** QC only: passed so far (null = legacy row, not tracked). */
  passedQty?: number | null;
  /** QC only: received − passed − rejected (null = unknown). */
  pendingQty?: number | null;
}

type QtyField = "quantity" | "tested_quantity";

interface SkuVariantQtyListProps {
  rows: SkuQtyRow[];
  values?: InventoryOpsSkuVariantLine[] | null;
  onChange: (lines: InventoryOpsSkuVariantLine[]) => void;
  disabled?: boolean;
  /** Label for the already-recorded column, e.g. "Received", "Rejected". */
  recordedLabel?: string;
  /** When true, zero is a valid entered quantity (QC rejections). */
  allowZero?: boolean;
  /** When false, hides the Remaining column (used for QC sections). Default true. */
  showRemaining?: boolean;
  /** When set, only checked rows can enter quantity. */
  selectedSkus?: Set<string>;
  onSelectionChange?: (sku: string, selected: boolean) => void;
  onBulkSelectionChange?: (skus: string[], selected: boolean) => void;
  errorMessage?: string;
  /**
   * QC entry: Received · Passed · Rejected · Pending columns with Tested and
   * Reject inputs. Line `tested_quantity` is capped at the row's maxQty and
   * line `quantity` (rejected) at tested (or maxQty on a reject-only line).
   */
  qcMode?: boolean;
  /** QC: label for the pending column (e.g. "To receive" when counting at QC). */
  pendingLabel?: string;
  /** QC: reject-only entry (legacy rows that can only be confirmed). */
  hideTestedInput?: boolean;
}

/**
 * Per-SKU quantity entry list used by the SKU-aware inventory ops mutations.
 */
const SkuVariantQtyList: React.FC<SkuVariantQtyListProps> = ({
  rows,
  values,
  onChange,
  disabled,
  recordedLabel = "Recorded",
  allowZero = false,
  showRemaining = true,
  selectedSkus,
  onSelectionChange,
  onBulkSelectionChange,
  errorMessage,
  qcMode = false,
  pendingLabel = "Pending",
  hideTestedInput = false,
}) => {
  const theme = useTheme();
  const useSelection = Boolean(
    selectedSkus && onSelectionChange && onBulkSelectionChange,
  );

  const isRowSelected = (sku: string) =>
    !useSelection || selectedSkus!.has(sku);

  const findLine = (row: SkuQtyRow) =>
    values?.find(
      (item) => item.sku === row.sku || item.variant_id === row.variant_id,
    );

  const getValue = (
    row: SkuQtyRow,
    field: QtyField = "quantity",
  ): number | "" => {
    const line = findLine(row);
    if (!line) return "";
    const value = line[field] ?? 0;
    if (allowZero && !qcMode) return value;
    return value > 0 ? value : "";
  };

  const handleQuantityChange = (
    row: SkuQtyRow,
    rawValue: string,
    field: QtyField = "quantity",
  ) => {
    if (!isRowSelected(row.sku)) return;

    try {
      let quantity = rawValue === "" ? 0 : Number(rawValue);
      if (Number.isNaN(quantity)) return;

      const existing = findLine(row);
      const rowMax = row.maxQty ?? row.remainingQty;
      let maxQty = rowMax != null ? Math.max(0, rowMax) : null;
      // QC: rejected is part of tested, so cap it at tested when entered.
      const existingTested = existing?.tested_quantity ?? 0;
      if (qcMode && field === "quantity" && existingTested > 0) {
        maxQty =
          maxQty == null ? existingTested : Math.min(maxQty, existingTested);
      }
      if (maxQty != null && quantity > maxQty) {
        quantity = maxQty;
      }

      const nextLine: InventoryOpsSkuVariantLine = {
        variant_id: row.variant_id,
        sku: row.sku,
        title: row.title ?? null,
        quantity: existing?.quantity ?? 0,
        ...(qcMode ? { tested_quantity: existingTested } : {}),
        [field]: quantity,
      };
      if (
        qcMode &&
        field === "tested_quantity" &&
        quantity > 0 &&
        nextLine.quantity > quantity
      ) {
        nextLine.quantity = quantity;
      }

      const otherLines = (values || []).filter(
        (line) => line.variant_id !== row.variant_id,
      );
      onChange([...otherLines, nextLine]);
    } catch (error) {
      // Ignore malformed input
    }
  };

  const sumEntered = (field: QtyField) =>
    (values || []).reduce((sum, line) => {
      if (useSelection && !selectedSkus!.has(line.sku)) return sum;
      const value = line[field] ?? 0;
      return sum + (allowZero || value > 0 ? value : 0);
    }, 0);
  const totalEntered = sumEntered("quantity");
  const totalTestedEntered = qcMode ? sumEntered("tested_quantity") : 0;
  const showTestedInput = qcMode && !hideTestedInput;

  // QC: only rows with unchecked qty can be entered.
  const canEnterRow = (row: SkuQtyRow) =>
    qcMode ? (row.maxQty ?? 0) > 0 : allowZero || (row.maxQty ?? 0) > 0;

  const hasReferenceQty = rows.some((row) => row.referenceQty !== undefined);
  const referenceLabel =
    rows.find((row) => row.referenceLabel)?.referenceLabel || "Ordered";
  const hasRemainingQty =
    showRemaining && !qcMode && rows.some((row) => row.remainingQty != null);

  // QC (allowZero): every SKU can be selected to record 0 rejection / clear.
  // Other sections: only SKUs with remaining qty.
  const selectableRows = useSelection
    ? rows.filter((row) => !disabled && canEnterRow(row))
    : [];

  const allSelectableSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selectedSkus!.has(row.sku));

  const someSelectableSelected = selectableRows.some((row) =>
    selectedSkus!.has(row.sku),
  );

  const handleSelectAll = (checked: boolean) => {
    if (!useSelection) return;
    onBulkSelectionChange!(
      selectableRows.map((row) => row.sku),
      checked,
    );
  };

  if (rows.length === 0) return null;

  const selectColSize = useSelection ? 1 : 0;
  const statColSize = qcMode ? 1 : 2;
  const referenceColSize = hasReferenceQty ? statColSize : 0;
  const recordedColSize = statColSize;
  const remainingColSize = hasRemainingQty ? 2 : 0;
  const qcStatColsSize = qcMode ? statColSize * 2 : 0;
  const qtyColSize = 2;
  const qtyInputsSize = showTestedInput ? qtyColSize * 2 : qtyColSize;
  const skuColSize =
    12 -
    selectColSize -
    referenceColSize -
    recordedColSize -
    remainingColSize -
    qcStatColsSize -
    qtyInputsSize;

  const headerCell = (label: string, size: number) => (
    <Grid2 size={size} sx={{ textAlign: "center" }}>
      <CustomTypography
        variant="heading7"
        color={theme.palette.customColors.greyTextColor}
        style={{ fontWeight: 600 }}
      >
        {label}
      </CustomTypography>
    </Grid2>
  );

  const statCell = (value: React.ReactNode, muted = false) => (
    <Grid2 size={statColSize} sx={{ textAlign: "center" }}>
      <CustomTypography
        variant="heading7"
        color={muted ? theme.palette.customColors.greyTextColor : undefined}
      >
        {value}
      </CustomTypography>
    </Grid2>
  );

  const qtyInputCell = (
    row: SkuQtyRow,
    field: QtyField,
    inputDisabled: boolean,
  ) => (
    <Grid2
      size={qtyColSize}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <CustomTextField
        type="number"
        allowDecimalValue={false}
        allowNegativeValue={false}
        borderColor={`0.1px solid ${theme.palette.customColors.greyDarkborder}`}
        value={getValue(row, field)}
        placeholder="00"
        width={qcMode ? "85%" : "70%"}
        onChange={(e) => handleQuantityChange(row, e.target.value, field)}
        disabled={inputDisabled}
      />
    </Grid2>
  );

  const toggleRow = (sku: string, nextSelected: boolean) => {
    if (!useSelection || !onSelectionChange) return;
    onSelectionChange(sku, nextSelected);
  };

  return (
    <Box sx={{ marginBottom: 2 }}>
      <Box
        sx={{
          border: `1px solid ${
            errorMessage
              ? theme.palette.error.main
              : theme.palette.customColors.disabledBorderColor
          }`,
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Grid2
          container
          columns={12}
          spacing={1}
          sx={{
            backgroundColor: theme.palette.customColors.greyBackground,
            padding: "0.625rem 1rem",
            alignItems: "center",
          }}
        >
          {useSelection ? (
            <Grid2
              size={selectColSize}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <Checkbox
                size="small"
                checked={allSelectableSelected}
                indeterminate={someSelectableSelected && !allSelectableSelected}
                disabled={disabled || selectableRows.length === 0}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (disabled || selectableRows.length === 0) return;
                  handleSelectAll(!allSelectableSelected);
                }}
                onChange={() => undefined}
              />
            </Grid2>
          ) : null}
          <Grid2 size={skuColSize}>
            <CustomTypography
              variant="heading7"
              color={theme.palette.customColors.greyTextColor}
              style={{ fontWeight: 600 }}
            >
              SKU
            </CustomTypography>
          </Grid2>
          {hasReferenceQty && (
            <Grid2 size={referenceColSize} sx={{ textAlign: "center" }}>
              <CustomTypography
                variant="heading7"
                color={theme.palette.customColors.greyTextColor}
                style={{ fontWeight: 600 }}
              >
                {referenceLabel}
              </CustomTypography>
            </Grid2>
          )}
          {qcMode ? (
            <>
              {headerCell("Passed", statColSize)}
              {headerCell(recordedLabel, recordedColSize)}
              {headerCell(pendingLabel, statColSize)}
              {showTestedInput ? headerCell("Tested", qtyColSize) : null}
              {headerCell("Reject", qtyColSize)}
            </>
          ) : (
            <>
              {headerCell(recordedLabel, recordedColSize)}
              {hasRemainingQty
                ? headerCell("Remaining", remainingColSize)
                : null}
              {headerCell("Qty", qtyColSize)}
            </>
          )}
        </Grid2>

        <Box sx={{ maxHeight: "18rem", overflowY: "auto" }}>
          {rows.map((row, index) => {
            const rowSelected = isRowSelected(row.sku);
            const checkboxDisabled = Boolean(disabled || !canEnterRow(row));
            const qtyDisabled = Boolean(
              disabled || (useSelection && !rowSelected) || !canEnterRow(row),
            );

            return (
              <React.Fragment key={row.sku}>
                {index > 0 && <Divider />}
                <Grid2
                  container
                  columns={12}
                  spacing={1}
                  sx={{
                    alignItems: "center",
                    padding: "0.5rem 1rem",
                    opacity: checkboxDisabled ? 0.65 : 1,
                    "&:hover": {
                      backgroundColor:
                        theme.palette.customColors.greyBackground,
                    },
                  }}
                >
                  {useSelection ? (
                    <Grid2
                      size={selectColSize}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minWidth: 40,
                        cursor: checkboxDisabled ? "not-allowed" : "pointer",
                      }}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (checkboxDisabled) return;
                        toggleRow(row.sku, !rowSelected);
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={rowSelected}
                        disabled={checkboxDisabled}
                        tabIndex={-1}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          if (checkboxDisabled) return;
                          toggleRow(row.sku, !rowSelected);
                        }}
                        onChange={() => undefined}
                      />
                    </Grid2>
                  ) : null}
                  <Grid2
                    size={skuColSize}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      cursor:
                        useSelection && !checkboxDisabled
                          ? "pointer"
                          : "default",
                    }}
                    onClick={() => {
                      if (!useSelection || checkboxDisabled) return;
                      toggleRow(row.sku, !rowSelected);
                    }}
                  >
                    <CustomTypography variant="heading7">
                      {row.title || row.sku}
                    </CustomTypography>
                    {row.title && (
                      <CustomTypography
                        variant="body2"
                        color={theme.palette.customColors.greyTextColor}
                      >
                        {row.sku}
                      </CustomTypography>
                    )}
                  </Grid2>
                  {hasReferenceQty && (
                    <Grid2 size={referenceColSize} sx={{ textAlign: "center" }}>
                      <CustomTypography
                        variant="heading7"
                        color={theme.palette.customColors.greyTextColor}
                      >
                        {row.referenceQty ?? "-"}
                      </CustomTypography>
                    </Grid2>
                  )}
                  {qcMode ? (
                    <>
                      {statCell(row.passedQty ?? "—")}
                      {statCell(row.recordedQty ?? 0)}
                      {statCell(row.pendingQty ?? "—", true)}
                      {showTestedInput
                        ? qtyInputCell(row, "tested_quantity", qtyDisabled)
                        : null}
                      {qtyInputCell(row, "quantity", qtyDisabled)}
                    </>
                  ) : (
                    <>
                      <Grid2
                        size={recordedColSize}
                        sx={{ textAlign: "center" }}
                      >
                        <CustomTypography variant="heading7">
                          {row.recordedQty ?? 0}
                        </CustomTypography>
                      </Grid2>
                      {hasRemainingQty ? (
                        <Grid2
                          size={remainingColSize}
                          sx={{ textAlign: "center" }}
                        >
                          <CustomTypography
                            variant="heading7"
                            color={theme.palette.customColors.greyTextColor}
                          >
                            {row.remainingQty ?? 0}
                          </CustomTypography>
                        </Grid2>
                      ) : null}
                      {qtyInputCell(row, "quantity", qtyDisabled)}
                    </>
                  )}
                </Grid2>
              </React.Fragment>
            );
          })}
        </Box>

        <Divider />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0.625rem 1rem",
            backgroundColor: theme.palette.customColors.greyBackground,
          }}
        >
          <CustomTypography
            variant="heading7"
            color={theme.palette.customColors.greyTextColor}
          >
            {rows.length} SKU{rows.length > 1 ? "s" : ""}
          </CustomTypography>
          <CustomTypography variant="heading7" style={{ fontWeight: 600 }}>
            {qcMode
              ? showTestedInput
                ? `Testing: ${totalTestedEntered} · Rejecting: ${totalEntered}`
                : `Rejecting: ${totalEntered}`
              : `Total Entered: ${totalEntered}`}
          </CustomTypography>
        </Box>
      </Box>
      <InlineFormError message={errorMessage} sx={{ mb: 0, mt: 0.75 }} />
    </Box>
  );
};

export default SkuVariantQtyList;
